import type { IconType } from 'react-icons'

import cx from 'classnames'
import { forwardRef, useRef } from 'react'
import { animated, config, useSpring } from 'react-spring'
import { useClickAway } from 'react-use'

export interface Action {
  id: string
  icon: IconType
  label: string
  onClick: () => void
}

export interface ContextMenuProps {
  position: {
    x: number
    y: number
  }

  actions?: Action[]
  direction?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'
  onClose: () => void
}

export let ContextMenu = forwardRef<HTMLDivElement, ContextMenuProps>(({ position, actions = [], direction = 'bottom-right', onClose }, ref): JSX.Element => {
  let innerRef = useRef<HTMLDivElement | null>(null)

  useClickAway(innerRef, () => {
    onClose()
  })

  let transform: string
  let className: string

  switch (direction) {
    case 'bottom-left':
      transform = `translate(calc(${position.x}px - 100%), ${position.y}px)`
      className = 'origin-top-right'
      break

    case 'bottom-right':
      transform = `translate(${position.x}px, ${position.y}px)`
      className = 'origin-top-left'
      break

    case 'top-left':
      transform = `translate(calc(${position.x}px - 100%), calc(${position.y}px - 100%))`
      className = 'origin-bottom-right'
      break

    case 'top-right':
      transform = `translate(${position.x}px, ${position.y}px)`
      className = 'origin-bottom-left'
      break
  }

  let styles = useSpring({
    from: {
      opacity: 0,
      transform: `${transform} scale(0.75)`
    },

    to: {
      opacity: 1,
      transform: `${transform} scale(1)`
    },

    config: config.stiff
  })

  return (
    <animated.div
      className={cx('absolute top-0 left-0 flex flex-col overflow-hidden bg-white rounded-lg border border-gray-200 shadow-lg origin-top-left z-10', className)}
      ref={el => {
        innerRef.current = el

        if (typeof ref === 'function') {
          ref(el)
          return
        }

        if (ref) {
          ref.current = el
        }
      }}
      style={styles}
    >
      {actions.map(action => (
        <Button
          key={action.id}
          icon={action.icon}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      ))}

      {/* <Button
        icon={MdOutlineCancel}
        onClick={() => {
          onClose()
        }}
      >
        Cancel
      </Button> */}
    </animated.div>
  )
})

ContextMenu.displayName = 'ContextMenu'

interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  icon: IconType
}

function Button ({ icon: Icon, children, ...rest }: ButtonProps): JSX.Element {
  return (
    <button
      type='button'
      className='flex flex-row items-center px-4 py-2.5 text-sm font-roboto font-medium text-gray-800 hover:bg-gray-100'
      {...rest}
    >
      <Icon
        size={18}
        className='text-gray-500'
      />

      <span className='ml-2 font-roboto text-sm font-normal'>
        {children}
      </span>
    </button>
  )
}
